import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';
import { addBreadcrumb } from '@sentry/react';
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

import { getAppsFlyerSdk } from './appsflyer';
import { appsFlyerBuffer } from './appsflyer.buffer';
import { AppsFlyerApi } from './appsflyer.types';

const defaultValue: AppsFlyerApi = {
  logEvent: appsFlyerBuffer.queueBufferEvent('logEvent'),
  setCustomerUserId: appsFlyerBuffer.queueBufferEvent('setCustomerUserId'),
};

const AppsFlyerContext = createContext<AppsFlyerApi>(defaultValue);

export const useAppsFlyer = () => useContext(AppsFlyerContext);

type AppsFlyerProviderProps = {
  appId: string;
  devKey: string;
  webDevKey: string | null;
  children: ReactNode;
};

const AppsFlyerProvider = ({ children, appId, devKey, webDevKey }: AppsFlyerProviderProps) => {
  const [sdk, setSdk] = useState<AppsFlyerApi>(defaultValue);

  useEffect(() => {
    const initializeSdk = async () => {
      const appsFlyerSdk = await getAppsFlyerSdk({
        appId,
        devKey,
        webDevKey,
      });

      if (appsFlyerSdk) {
        setSdk(appsFlyerSdk);
      }
    };

    initializeSdk();
  }, [appId, devKey, webDevKey]);

  return <AppsFlyerContext.Provider value={sdk}>{children}</AppsFlyerContext.Provider>;
};

type AppsFlyerProviderWrapper = {
  children: ReactNode;
};

export const AppsFlyerProviderWrapper = ({ children }: AppsFlyerProviderWrapper) => {
  const {
    config: { appsFlyer },
  } = useConfiguration();
  const isAppsFlyerEnabled = appsFlyer?.enabled;
  const areAllRequiredKeysPresent = appsFlyer?.enabled && appsFlyer?.appId && appsFlyer?.devKey;

  if (isAppsFlyerEnabled && !areAllRequiredKeysPresent) {
    addBreadcrumb({
      message: 'AppsFlyer is enabled but some required keys are missing. Please check your configuration.',
    });
  }

  if (isAppsFlyerEnabled && areAllRequiredKeysPresent) {
    return (
      <AppsFlyerProvider appId={appsFlyer.appId} devKey={appsFlyer.devKey} webDevKey={appsFlyer.webDevKey}>
        {children}
      </AppsFlyerProvider>
    );
  }

  return children;
};
